import React, { useState, useEffect, useCallback, useMemo } from "react";
import PageHeader from "../components/shared/PageHeader";
import PageContainer from "../components/shared/PageContainer";
import { Button, Select, Spinner, TextInput, Th } from "../components/ui";
import { ordersApi, entityApi } from "../services/api";
import { Order } from "../types";

interface Supplier {
  id: number;
  businessName: string;
  businessDisplayName?: string;
}

interface Client {
  id: number;
  businessName: string;
  businessDisplayName?: string;
}

const toIsoDate = (d: Date): string => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};

const todayIso = (): string => toIsoDate(new Date());

const daysAgoIso = (days: number): string => {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return toIsoDate(d);
};

const sevenDaysAgoIso = (): string => daysAgoIso(6);

type PresetKey = "1d" | "7d" | "30d" | "custom";

const PRESETS: { key: PresetKey; label: string; days: number }[] = [
  { key: "1d", label: "Today", days: 0 },
  { key: "7d", label: "Last 7 Days", days: 6 },
  { key: "30d", label: "Last 30 Days", days: 29 },
];

const formatGBP = (value: number | null | undefined): string => {
  if (value == null || Number.isNaN(value)) return "";
  return `£ ${value.toFixed(2)}`;
};

const humanizeType = (type?: string): string => {
  if (!type) return "";
  return type
    .split("-")
    .map((s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s))
    .join(" ");
};

const toNumberOrNull = (v: unknown): number | null => {
  if (v === undefined || v === null || v === "") return null;
  const n = typeof v === "number" ? v : parseFloat(String(v));
  return Number.isNaN(n) ? null : n;
};

interface SummaryRow {
  key: string;
  productName: string;
  size: string;
  dailyQty: Record<string, number>;
  totalQty: number;
  unitPrice: number | null;
  totalAmount: number;
}

// The first column stays put while the day columns scroll sideways.
const STICKY_COL = "bg-surface sticky left-0 z-[1]";

const MonthlySummary: React.FC = () => {
  const [orders, setOrders] = useState<Order[]>([]);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [startDate, setStartDate] = useState<string>(sevenDaysAgoIso());
  const [endDate, setEndDate] = useState<string>(todayIso());
  const [activePreset, setActivePreset] = useState<PresetKey>("7d");
  const [selectedSupplierId, setSelectedSupplierId] = useState<number | null>(null);
  const [selectedClientId, setSelectedClientId] = useState<number | null>(null);

  const applyPreset = (preset: typeof PRESETS[number]) => {
    setStartDate(daysAgoIso(preset.days));
    setEndDate(todayIso());
    setActivePreset(preset.key);
  };

  const fetchData = useCallback(async (showLoading = false) => {
    if (showLoading) setLoading(true);
    else setRefreshing(true);
    try {
      const [orderResult, supplierResult, clientResult] = await Promise.all([
        ordersApi.getOrders(),
        entityApi.getAllSuppliers(),
        entityApi.getAllClients(),
      ]);
      const orderList = Array.isArray(orderResult) ? orderResult : [];
      const supplierList: Supplier[] = Array.isArray(supplierResult) ? supplierResult : [];
      const clientList: Client[] = Array.isArray(clientResult) ? clientResult : [];
      setOrders(orderList);
      setSuppliers(supplierList);
      setClients(clientList);
    } catch (error) {
      console.error("Failed to fetch summary data:", error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, []);

  useEffect(() => {
    fetchData(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const dayKeys = useMemo<string[]>(() => {
    if (!startDate || !endDate) return [];
    const start = new Date(`${startDate}T00:00:00`);
    const end = new Date(`${endDate}T00:00:00`);
    if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) return [];
    if (end < start) return [];
    const list: string[] = [];
    const cursor = new Date(start);
    for (let i = 0; i < 90 && cursor <= end; i++) {
      list.push(toIsoDate(cursor));
      cursor.setDate(cursor.getDate() + 1);
    }
    return list;
  }, [startDate, endDate]);

  const formatDayHeader = (iso: string): string => {
    const d = new Date(`${iso}T00:00:00`);
    if (Number.isNaN(d.getTime())) return iso;
    const day = String(d.getDate()).padStart(2, "0");
    const mon = String(d.getMonth() + 1).padStart(2, "0");
    return `${day}/${mon}`;
  };

  const rows = useMemo<SummaryRow[]>(() => {
    const map = new Map<string, SummaryRow>();
    const dayKeySet = new Set(dayKeys);

    const upsert = (
      name: string,
      code: string,
      size: string,
      dayKey: string,
      qty: number,
      unitPrice: number | null,
    ) => {
      const key = `${name}|${code}|${size}`;
      let row = map.get(key);
      if (!row) {
        row = {
          key,
          productName: name,
          size,
          dailyQty: {},
          totalQty: 0,
          unitPrice: null,
          totalAmount: 0,
        };
        map.set(key, row);
      }
      row.dailyQty[dayKey] = (row.dailyQty[dayKey] || 0) + qty;
      row.totalQty += qty;
      if (unitPrice != null) row.unitPrice = unitPrice;
    };

    orders.forEach((order) => {
      if (selectedSupplierId != null && order.supplierId !== selectedSupplierId) return;
      if (selectedClientId != null && order.clientId !== selectedClientId) return;
      if (!order.dateOrdered) return;
      const ordered = new Date(order.dateOrdered);
      if (Number.isNaN(ordered.getTime())) return;
      const dayKey = toIsoDate(ordered);
      if (!dayKeySet.has(dayKey)) return;

      let parsed: any = null;
      if (order.formData) {
        try {
          parsed = JSON.parse(order.formData);
        } catch {
          parsed = null;
        }
      }
      const fd = parsed || {};

      if (Array.isArray(fd.products) && fd.products.length > 0) {
        fd.products.forEach((p: any) => {
          const pPrice = toNumberOrNull(p?.price);
          const pQuantity = toNumberOrNull(p?.quantity);

          let qty: number;
          let unitPrice: number | null;
          if (pPrice != null) {
            unitPrice = pPrice;
            qty = pQuantity ?? 1;
          } else if (pQuantity != null) {
            unitPrice = pQuantity;
            qty = 1;
          } else {
            unitPrice = null;
            qty = 0;
          }

          const name = typeof p?.name === "string" ? p.name : "";
          const code = typeof p?.code === "string" ? p.code : "";
          const size = typeof p?.size === "string" ? p.size : "";
          if (!name && !code) return;
          upsert(name, code, size, dayKey, qty, unitPrice);
        });
      } else {
        const fdPrice = toNumberOrNull(fd.price);
        const fdQuantity = toNumberOrNull(fd.quantity);
        const orderQuantity = toNumberOrNull(order.quantity);
        const orderPrice = toNumberOrNull(order.price);

        let qty: number;
        let unitPrice: number | null;
        if (fdPrice != null) {
          unitPrice = fdPrice;
          qty = fdQuantity ?? orderQuantity ?? 1;
        } else if (fdQuantity != null) {
          unitPrice = fdQuantity;
          qty = 1;
        } else {
          unitPrice = orderPrice;
          qty = orderQuantity ?? 1;
        }

        const name = humanizeType(order.type);
        const code =
          (typeof fd.code === "string" && fd.code) || order.code || "";
        const size =
          (typeof fd.size === "string" && fd.size) || order.size || "";
        if (!name && !code) return;
        upsert(name, code, size, dayKey, qty, unitPrice);
      }
    });

    const result = Array.from(map.values());
    result.forEach((r) => {
      r.totalAmount = r.unitPrice != null ? r.totalQty * r.unitPrice : 0;
    });
    result.sort((a, b) => a.productName.localeCompare(b.productName));
    return result;
  }, [orders, dayKeys, selectedSupplierId, selectedClientId]);

  const grandTotal = useMemo(() => {
    let qty = 0;
    let amount = 0;
    rows.forEach((r) => {
      qty += r.totalQty;
      amount += r.totalAmount;
    });
    return { qty, amount };
  }, [rows]);

  const formatQty = (n: number): string => {
    if (!n) return "";
    return n % 1 === 0 ? String(n) : n.toFixed(2);
  };

  const presetClass = (isActive: boolean) =>
    `rounded-xl px-3 py-1.5 text-xs font-medium transition-colors ${
      isActive
        ? "bg-brand/10 text-brand-strong ring-1 ring-brand/20"
        : "border-hairline bg-surface hover:bg-elevated border text-zinc-600 hover:text-zinc-900"
    }`;

  return (
    <PageContainer>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <PageHeader title="Summary" subTitle="Sales by Product" />

        <Button
          variant="outline"
          onClick={() => fetchData()}
          disabled={refreshing || loading}
          className="shrink-0"
        >
          {refreshing ? (
            <>
              <Spinner size="size-4" />
              Refreshing…
            </>
          ) : (
            <>
              <i className="bi bi-arrow-clockwise" />
              Refresh
            </>
          )}
        </Button>
      </div>

      <section className="panel overflow-hidden">
        <header className="border-hairline/60 space-y-4 border-b p-5">
          <div className="flex flex-wrap gap-2">
            {PRESETS.map((p) => (
              <button
                key={p.key}
                type="button"
                className={presetClass(activePreset === p.key)}
                onClick={() => applyPreset(p)}
              >
                {p.label}
              </button>
            ))}
            <button
              type="button"
              className={presetClass(activePreset === "custom")}
              onClick={() => setActivePreset("custom")}
            >
              Custom Range
            </button>
          </div>

          {/* One row of filters: each control flexes from a sensible minimum,
              so they sit on a single line on desktop and wrap only when
              narrow. */}
          <div className="flex flex-wrap items-end gap-3 [&>*]:min-w-44 [&>*]:flex-1">
            <Select
              label="Supplier"
              value={selectedSupplierId ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedSupplierId(v === "" ? null : parseInt(v, 10));
              }}
            >
              <option value="">All Suppliers</option>
              {suppliers.map((s) => (
                <option key={s.id} value={s.id}>
                  {s.businessDisplayName || s.businessName}
                </option>
              ))}
            </Select>

            <Select
              label="Branch"
              value={selectedClientId ?? ""}
              onChange={(e) => {
                const v = e.target.value;
                setSelectedClientId(v === "" ? null : parseInt(v, 10));
              }}
            >
              <option value="">All Branches</option>
              {clients.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.businessDisplayName || c.businessName}
                </option>
              ))}
            </Select>

            <TextInput
              label="Start Date"
              type="date"
              value={startDate}
              max={endDate || undefined}
              onChange={(e) => {
                setStartDate(e.target.value);
                setActivePreset("custom");
              }}
            />

            <TextInput
              label="End Date"
              type="date"
              value={endDate}
              min={startDate || undefined}
              onChange={(e) => {
                setEndDate(e.target.value);
                setActivePreset("custom");
              }}
            />
          </div>
        </header>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr>
                <Th className={STICKY_COL}>Product</Th>
                <Th>Size</Th>
                {dayKeys.map((d) => (
                  <Th key={d} className="min-w-16 text-right">
                    {formatDayHeader(d)}
                  </Th>
                ))}
                <Th className="text-right">Total Qty</Th>
                <Th className="text-right">Unit Price</Th>
                <Th className="text-right">Total Amount</Th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={dayKeys.length + 5}
                    className="px-4 py-12 text-center"
                  >
                    <span className="inline-flex items-center gap-2 text-sm text-zinc-500">
                      <Spinner size="size-4" />
                      Loading summary…
                    </span>
                  </td>
                </tr>
              ) : dayKeys.length === 0 ? (
                <tr>
                  <td
                    colSpan={5}
                    className="px-4 py-12 text-center text-sm text-zinc-500"
                  >
                    Select a valid date range.
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td
                    colSpan={dayKeys.length + 5}
                    className="px-4 py-12 text-center text-sm text-zinc-500"
                  >
                    No orders found for the selected date range.
                  </td>
                </tr>
              ) : (
                rows.map((r) => (
                  <tr key={r.key} className="hover:bg-elevated/60 transition-colors">
                    <td
                      className={`border-hairline/50 border-b px-4 py-3 font-medium text-zinc-900 ${STICKY_COL}`}
                    >
                      {r.productName}
                    </td>
                    <td className="border-hairline/50 border-b px-4 py-3 text-zinc-500">
                      {r.size}
                    </td>
                    {dayKeys.map((d) => (
                      <td
                        key={d}
                        className="border-hairline/50 border-b px-4 py-3 text-right text-zinc-500 tabular-nums"
                      >
                        {formatQty(r.dailyQty[d] || 0)}
                      </td>
                    ))}
                    <td className="border-hairline/50 border-b px-4 py-3 text-right font-medium text-zinc-900 tabular-nums">
                      {formatQty(r.totalQty)}
                    </td>
                    <td className="border-hairline/50 border-b px-4 py-3 text-right text-zinc-700 tabular-nums">
                      {r.unitPrice != null ? formatGBP(r.unitPrice) : ""}
                    </td>
                    <td className="border-hairline/50 border-b px-4 py-3 text-right text-zinc-700 tabular-nums">
                      {r.unitPrice != null ? formatGBP(r.totalAmount) : ""}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
            {!loading && rows.length > 0 && (
              <tfoot>
                <tr className="border-hairline bg-canvas/60 border-t font-medium text-zinc-900">
                  <td className={`px-4 py-3 ${STICKY_COL}`}>Total</td>
                  <td className="px-4 py-3"></td>
                  {dayKeys.map((d) => (
                    <td key={d} className="px-4 py-3"></td>
                  ))}
                  <td className="px-4 py-3 text-right tabular-nums">
                    {formatQty(grandTotal.qty)}
                  </td>
                  <td className="px-4 py-3"></td>
                  <td className="px-4 py-3 text-right tabular-nums">
                    {formatGBP(grandTotal.amount)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </section>
    </PageContainer>
  );
};

export default MonthlySummary;
